<template>
  <div class="mb-1">
    <v-row dense>
      <v-col cols="12" md="12" lg="6">
        <div class="numerai-paper pa-6 pb-4 mx-auto fill-height">
          <v-row dense class="mx-0">
            <v-col class="pa-0" cols="12">
              <div class="d-flex full-width profile-meta">
                <div
                  class="profile-pic-container"
                  :class="{ editable: isModelOwner }"
                >
                  <div
                    class="profile-pic"
                    :class="{ default: !profile.profileUrl }"
                    :style="
                      profile.profileUrl
                        ? { backgroundImage: `url('${profile.profileUrl}')` }
                        : null
                    "
                  ></div>
                  <div v-if="isModelOwner" class="profile-pic-edit">
                    <NumeraiUploadFile
                      :auth-endpoint="$client.getProfileUploadAuth"
                      :model-id="profile.id"
                      :size-limit="200000"
                      success-text="Profile image uploaded"
                      @upload="profileChangeCallback"
                    >
                      <button class="numerai-button">
                        Upload photo
                      </button>
                    </NumeraiUploadFile>
                  </div>
                </div>
                <div>
                  <div class="username mb-2" :title="profile.username">
                    {{ profile.username }}
                    <img
                      v-if="profile.team"
                      class="team-badge"
                      :class="{ inverted: isDarkMode }"
                      src="@/assets/Numerai-Sigil-Black.png"
                      alt="Model created by the Numerai team"
                      title="Model created by the Numerai team"
                    />
                  </div>
                  <div class="subtext mb-3 woke">
                    <span
                      >Woke: <NumeraiShortDate :date="profile.startDate"
                    /></span>
                    <span></span>&nbsp;|&nbsp;
                    <router-link
                      :to="`/~${profile.accountName}`"
                      class="profileLink"
                    >
                      <span class="hov">Account summary »</span>
                    </router-link>
                  </div>
                  <div class="subtext mb-3">
                    <ManageUserLink
                      v-if="isModelOwner"
                      :model-id="profile.id"
                      :profile="profile"
                      :fetch-profile="profileChangeCallback"
                    >
                      <NumeraiTooltip text="Edit Profile Link">
                        <v-icon class="edit-icon">mdi-pencil-box</v-icon>
                      </NumeraiTooltip>
                    </ManageUserLink>
                    <ExternalLink
                      v-if="profile.linkUrl"
                      :url="profile.linkUrl"
                      :text="profile.linkText"
                    />
                  </div>
                  <div class="subtextbio mb-3 d-sm-block">
                    <ManageUserBio
                      v-if="isModelOwner"
                      :model-id="profile.id"
                      :profile="profile"
                      :fetch-profile="profileChangeCallback"
                      class="d-sm-none"
                    >
                      <NumeraiTooltip text="Edit Model Bio">
                        <v-icon class="edit-icon">mdi-pencil-box</v-icon>
                      </NumeraiTooltip>
                    </ManageUserBio>
                    {{ profile.bio }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col cols="12" md="6" lg="3">
        <div class="numerai-paper pa-6 pb-4 fill-height">
          <div class="d-flex two-column align-center mb-3">
            <div class="numerai-section-heading">
              Performance
            </div>
          </div>
          <table class="pt-0 ma-0 stats-table">
            <tr>
              <th class="stats-heading"></th>
              <th class="stats-heading font-weight">1Y Avg</th>
              <th class="stats-heading">Rank</th>
            </tr>
            <tr
              v-for="(obj, scoreKey) in tournamentScoresConfig"
              :key="scoreKey"
            >
              <td class="stats-box-title">
                <span :class="getRankScoreClass(scoreKey)">
                  <NumeraiTooltip :text="obj.scoreTooltip">
                    {{ obj.displayName.toUpperCase() }}
                  </NumeraiTooltip>
                </span>
              </td>
              <td
                class="stats"
                :class="getReputation(scoreKey) ? '' : 'null-number'"
              >
                <div :class="getRankScoreClass(scoreKey)">
                  {{
                    formatScore(getReputation(scoreKey))
                      ? formatScore(getReputation(scoreKey))
                      : null
                  }}
                </div>
              </td>
              <td :class="getRank(scoreKey) ? '' : 'null-number'">
                <div :class="getRankScoreClass(scoreKey)">
                  {{ getRank(scoreKey) }}
                </div>
              </td>
            </tr>
          </table>

          <div class="numerai-section-heading pt-4 pb-4">
            Current Model Correlations
          </div>
          <table class="pt-0 ma-0 stats-table">
            <tr v-for="(obj, scoreKey) in metaScoresConfig" :key="scoreKey">
              <td class="stats-box-title">
                <span :class="getRankScoreClass(scoreKey)">
                  <NumeraiTooltip :text="obj.scoreTooltip">
                    {{ obj.displayName.toUpperCase() }}
                  </NumeraiTooltip>
                </span>
              </td>
              <td
                class="stats"
                :class="getLatestScore(scoreKey) ? '' : 'null-number'"
              >
                <div :class="getRankScoreClass(scoreKey)">
                  <NumeraiTooltip
                    :text="
                      'Percentile: ' +
                        formatPercentile(getLatestScorePercentile(scoreKey))
                    "
                  >
                    {{
                      formatScore(getLatestScore(scoreKey))
                        ? formatScore(getLatestScore(scoreKey))
                        : null
                    }}
                  </NumeraiTooltip>
                </div>
              </td>
            </tr>
          </table>
        </div>
      </v-col>
      <v-col cols="12" md="6" lg="3">
        <div class="numerai-paper pa-6 pb-4 fill-height">
          <div class="d-flex two-column align-center mb-3">
            <div class="numerai-section-heading">
              Stake
            </div>
            <div class="color-nmr numerai-section-heading">
              {{ stakeValue }}
            </div>
          </div>
          <table class="ma-0 pt-0 stats-table">
            <tr>
              <th class="stats-heading"></th>
              <th class="stats-heading font-weight"></th>
              <th class="stats-heading"></th>
            </tr>
            <tr>
              <td class="stats-box-title">
                1 Day Return
              </td>
              <td class="stats delta" :class="makeNumberStyle(oneDayReturns)">
                {{ removeMinusSign(formatPercent(oneDayReturns)) }}
              </td>
            </tr>
            <tr>
              <td class="stats-box-title">3 Month Return</td>
              <td
                class="stats delta"
                :class="makeNumberStyle(threeMonthsReturns)"
              >
                {{ removeMinusSign(formatPercent(threeMonthsReturns)) }}
              </td>
            </tr>
            <tr>
              <td class="stats-box-title">1 Year Return</td>
              <td class="stats delta" :class="makeNumberStyle(oneYearReturns)">
                {{ removeMinusSign(formatPercent(oneYearReturns)) }}
              </td>
            </tr>
            <tr>
              <td class="stats-box-title">All Time Return</td>
              <td class="stats delta" :class="makeNumberStyle(allTimeReturns)">
                {{ removeMinusSign(formatPercent(allTimeReturns)) }}
              </td>
            </tr>
            <tr>
              <td class="stats-box-title">Multipliers</td>
              <td class="stats">
                {{
                  formatStakeType(
                    stakeInfo.corrMultiplier,
                    stakeInfo.mmcMultiplier,
                    stakeInfo.tcMultiplier,
                  )
                }}
              </td>
            </tr>
            <tr>
              <td class="stats-box-title">
                <numerai-tooltip
                  text="The weight of this model in the Numerai Meta Model"
                  >Meta Model Control</numerai-tooltip
                >
              </td>
              <td class="stats">
                <numerai-tooltip :text="control + '%'">
                  {{ formatScore(control) }}%
                </numerai-tooltip>
              </td>
            </tr>
          </table>
        </div>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import _ from 'lodash'
import {
  NumeraiShortDate,
  NumeraiTooltip,
  NumeraiUploadFile,
} from '@/components/shared'
import ManageUserBio from '@/components/profile/ManageUserBio'
import ManageUserLink from '@/components/profile/ManageUserLink'
import ExternalLink from '../shared/ExternalLink.vue'

import { mapState } from 'vuex'

import {
  formatScore,
  formatPercent,
  formatPercentile,
  formatStakeType,
  makeNumberStyle,
  removeMinusSign,
  isAppCryptoSignals,
  getTournamentNumber,
  getTournamentScoresConfig,
} from '@/lib/utils'

export default {
  components: {
    NumeraiShortDate,
    NumeraiTooltip,
    NumeraiUploadFile,
    ManageUserBio,
    ManageUserLink,
    ExternalLink,
  },
  props: {
    profile: { type: Object, default: null },
    profileChangeCallback: { type: Function, default: null },
    isModelOwner: { type: Boolean, default: false },
    isDarkMode: { type: Boolean, default: false },
  },

  computed: {
    ...mapState(['currentRoundInfo']),
    tournamentScoresConfig() {
      const filterKey = 'showProfileTables'
      return getTournamentScoresConfig(getTournamentNumber(), filterKey)
    },
    metaScoresConfig() {
      const filterKey = 'showMetaScore'
      let config = getTournamentScoresConfig(getTournamentNumber(), filterKey)
      return config
    },
    maxCorrMultiplier() {
      return parseFloat(_.get(this.currentRoundInfo, 'maxCorrMultiplier'))
    },
    maxMmcMultiplier() {
      return parseFloat(_.get(this.currentRoundInfo, 'maxMmcMultiplier'))
    },
    maxTcMultiplier() {
      return parseFloat(_.get(this.currentRoundInfo, 'maxTcMultiplier'))
    },
    oneDayReturns() {
      return _.get(this.profile, 'latestReturns.oneDay', null)
    },
    threeMonthsReturns() {
      return _.get(this.profile, 'latestReturns.threeMonths', null)
    },
    oneYearReturns() {
      return _.get(this.profile, 'latestReturns.oneYear', null)
    },
    allTimeReturns() {
      return _.get(this.profile, 'latestReturns.allTime', null)
    },
    control() {
      return _.get(this.profile, 'control', null) * 100 + ''
    },
    stakeValue() {
      var stake = this.profile.stakeValue

      const total = _.max([0, parseFloat(stake) || 0])
      if (Math.abs(total) < 1 && total !== 0) {
        // If number is a fraction, use toPrecision
        return total.toPrecision(3) + ' NMR'
      }
      // else round
      return _.round(total, 2).toLocaleString() + ' NMR'
    },
    stakeInfo() {
      return _.get(this.profile, 'stakeInfo', null)
    },
  },
  async created() {
    try {
      this.$store.dispatch('fetchCurrentRoundInfo', getTournamentNumber())
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    formatScore,
    formatPercent,
    formatPercentile,
    formatStakeType,
    makeNumberStyle,
    removeMinusSign,
    isAppCryptoSignals,
    getRank(scoreKey) {
      if (!this.profile.latestRanks) {
        return null
      }
      return this.profile.latestRanks[scoreKey]
    },
    getRankScoreClass(scoreKey) {
      if (scoreKey === 'tc') {
        return 'color-text-tc text_weight_600'
      }
      if (scoreKey === 'v2_corr20') {
        return 'text_weight_600'
      }
      if (scoreKey === 'fnc_v4') {
        return 'text_weight_600'
      }
      if (scoreKey === 'mmc' || scoreKey === 'mmc60') {
        return 'mmc text_weight_600'
      }
      return ''
    },
    getReputation(scoreKey) {
      if (!this.profile.latestReps) {
        return null
      }
      return this.profile.latestReps[scoreKey]
    },
    getLatestScore(scoreKey) {
      if (!this.profile.latestSubmissionScores) {
        return null
      }
      for (const score of this.profile.latestSubmissionScores) {
        if (score.displayName === scoreKey) {
          return score.value
        }
      }
    },
    getLatestScorePercentile(scoreKey) {
      if (!this.profile.latestSubmissionScores) {
        return null
      }
      for (const score of this.profile.latestSubmissionScores) {
        if (score.displayName === scoreKey) {
          return score.percentile
        }
      }
    },
  },
}
</script>

<style scoped>
.two-column {
  margin-bottom: 1.8em;
  flex-direction: row;
  justify-content: space-between;
}
.hov:hover {
  cursor: pointer;
  text-decoration: underline;
}

.inverted {
  filter: invert(1);
}
.subtext {
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-tertiary);
  text-decoration: none;
  margin-bottom: 0.5em;
}

.bio {
  max-width: 350px;
  color: var(--color-primary);
  word-break: break-word;
  line-height: 1.4;
}

.link {
  color: var(--color-primary);
  text-decoration: underline;
}

.stats-table {
  width: 100%;
  margin-top: 1em;
  border-collapse: collapse;
  cursor: default;
}

.stats-table td {
  border-bottom: 1px solid var(--paper-border-color);
  cursor: default;
}

.stats-table tr:last-child td {
  border-bottom: none;
  cursor: default;
}

.stats-table td {
  font-family: var(--number-font);
  cursor: default;
}
.stats-table th {
  font-family: var(--ui-font);
  cursor: default;
}

.stats-table th,
.stats-table td {
  padding: 0.3em 0;
  text-align: right;
  height: 30px;
  overflow: hidden;
  cursor: default;
}

td.stats-box-title {
  font-weight: 500;
  font-family: var(--ui-font);
  text-align: left;
}

.stats {
  /* font-weight: 500; */
  color: var(--color-secondary);
  font-family: var(--number-font);
}

.profile-meta {
  align-items: flex-start;
}

a.profileLink {
  text-decoration: underline;
  color: var(--color-primary);
}

.edit-icon {
  color: var(--color-secondary);
}
.edit-icon:hover {
  cursor: pointer;
}

.profile_picture_placeholder {
  background-color: var(--paper-background-highlight1);
  border-radius: var(--radius);
  text-align: center;
}

.profile-pic-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 40px;
  flex-direction: column;
}

.profile-pic-container.editable:hover .profile-pic-edit {
  opacity: 1;
}

.profile-pic {
  flex-shrink: 0;
  background-position: center;
  background-size: cover;
  height: 225px;
  width: 225px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.profile-pic-edit {
  transition: all 0.2s;
  opacity: 0;
  background-color: rgba(0, 0, 0, 0.2);
  position: absolute;
  width: 225px;
  height: 225px;
  border-radius: 10px;
  top: 0;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-pic.default {
  background-image: var(--default_profile_picture);
}

.medal_box {
  display: flex;
  flex-direction: row;
  margin-right: 16px;
  align-items: center;
  white-space: nowrap;
}

.tooltip_row {
  margin-bottom: 0.4em;
}

.smaller_text {
  font-size: 1em;
  opacity: 0.8;
}

.null-number::before {
  font-size: 1em;
  content: '—';
}

.username {
  font-weight: 500;
  font-size: 32px;
  text-transform: uppercase;
  line-height: 1.1;
  margin-left: -2px;
  font-family: 'Erbaum', 'Roboto', sans-serif;
  color: var(--color-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  text-decoration: none;
}
.team-badge {
  display: inline-flex;
  height: 30px;
  width: 30px;
}
.profileImage {
  max-height: 300px;
  width: 250px;
  border: 1px solid var(--paper-background-color);
  background-color: var(--paper-background-color);
  box-sizing: border-box;
  border-radius: 4px;
  object-fit: contain;
  object-position: top;
}
.profile_picture_default {
  background-image: var(--default_profile_picture);
  background-size: cover;
  height: 250px;
}

@media screen and (max-width: 1500px) {
  .username {
    font-size: 36px;
  }
  .team-badge {
    display: inline-flex;
    height: 24px;
    width: 24px;
  }
}

@media screen and (max-width: 1350px) {
  .username {
    font-size: 24px;
  }
  .team-badge {
    height: 22px;
    width: 22px;
  }
}

@media screen and (max-width: 1280px) {
  .username {
    font-size: 24px;
  }
  .team-badge {
    height: 34px;
    width: 34px;
  }
  .profile-meta {
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media screen and (max-width: 780px) {
  .profileImage {
    max-height: 100px;
    max-width: 100px;
  }
  .username {
    font-size: 24px;
  }
  .team-badge {
    height: 22px;
    width: 22px;
  }
}

@media screen and (max-width: 600px) {
  .woke {
    white-space: pre-line;
  }
  .profile-pic-container {
    margin-right: 0;
    margin-bottom: 40px;
  }
}

@media screen and (max-width: 300px) {
  .username {
    font-size: 18px;
  }
}
</style>
